 <template>
  <InisoftContainer screen="logins">
    <main class="logins-wrapper" role="main">
      <div class="logins-wrapper__column">
        <h1 class="sr-only">ADVISGO - 
          {{ $t('landingPage.landingPageTitle') }}
        </h1>

        <mq-responsive group>  
          <template #xs>
            <product-logo />
            <flow-pattern-header-right />
          </template>

          <template #sm>
            <product-logo />
            <flow-pattern-header-right-wide />
          </template>

          <template #md+>
            <product-icon-logo />
          </template>
        </mq-responsive>

        <div v-if="!isUserAuthenticated && !landingPageError" class="landing-page">
          <h2>{{ $t('landingPage.connecting') }}</h2>
          <p>{{ $t('landingPage.pleaseWaitAuthenticating') }}</p>
        </div>

        <div v-else-if="isUserAuthenticated && !landingPageError" class="landing-page">
          <h2>{{ $t('landingPage.welcome') }}</h2>
          <p>{{ $t('landingPage.pleaseWait') }}</p>
        </div>

        <div v-else-if="landingPageError" class="error-message">
          <p>{{landingPageErrorType}}</p>
          <p>{{ $t('landingPage.error') }}</p>
        </div>

        <section class="login-footer">
          <small>{{ $t("common.softwareBy") }} <a href="https://www.inisoft.com/" target="_blank">Inisoft</a></small>
        </section>

      </div>

      <div class="logins-wrapper__brand-block">              
        <mq-responsive group>  
          <template #md+>
            <flow-pattern-BB-top-left />
            <flow-pattern-BB-top-right />
            <flow-pattern-BB-bottom-right />
            <flow-pattern-BB-bottom-left />  
          </template>
        </mq-responsive>
        <product-logo />
      </div>
    </main>
  </InisoftContainer>
</template>

<script lang="ts" setup>
import { useTheme } from 'vuetify'
import { useThemePreference } from '@/composables/useThemePreference'
const theme = useTheme()
useThemePreference(theme)

theme.global.name.value = 'defaultTheme'
</script>

<script lang="ts">
import InisoftContainer from '@/components/layout/InisoftContainer.vue';
import ProductLogo from '@/components/shared/ProductLogoComponent.vue';
import ProductIconLogo from '@/components/shared/ProductIconLogoComponent.vue';
import FlowPatternHeaderRight from '@/components/shared/flows/FlowPatternHeaderRight.vue';
import FlowPatternHeaderRightWide from '@/components/shared/flows/FlowPatternHeaderRightWide.vue';
import FlowPatternBBTopLeft from '@/components/shared/flows/FlowPatternBBTopLeft.vue';
import FlowPatternBBBottomRight from '@/components/shared/flows/FlowPatternBBBottomRight.vue';
import FlowPatternBBTopRight from '@/components/shared/flows/FlowPatternBBTopRight.vue';
import FlowPatternBBBottomLeft from '@/components/shared/flows/FlowPatternBBBottomLeft.vue';
import { useAuthenticationStore } from '@/stores/authentication';
import StartAuthQuery from '@/common/api/queries/Genesys/StartAuthQuery';
import HandleCallbackQuery from '@/common/api/queries/Genesys/HandleCallbackQuery';

export default {
    name: 'LandingPage',
    components: {
        InisoftContainer,
        ProductLogo,
        ProductIconLogo,
        FlowPatternHeaderRight,
        FlowPatternHeaderRightWide,
        FlowPatternBBTopLeft,
        FlowPatternBBTopRight,
        FlowPatternBBBottomRight,
        FlowPatternBBBottomLeft
    },
    mounted() {
        // Prevent duplicate authentication attempts
        if (this.authInProgress) {
            console.log('Authentication already in progress, skipping duplicate mount');
            return;
        }
        this.authInProgress = true;
    
        const pathParts = window.location.pathname.split('/').filter(part => part);
        
        // Check if 'widget' is in the URL path
        const isWidgetMode = pathParts.includes('widget');
        if (isWidgetMode) {
            this.authStore.setIsWidgetMode(true);
            console.log('Widget mode detected - Advisgo launched as widget');
        } else {
            this.authStore.setIsWidgetMode(false);
            console.log('App mode detected - Advisgo launched as standalone app');
        }
        
        // Extract tenant name (first part that's not 'widget' or 'landing-page')
        const tenantNameFromUrl = pathParts.find(part => part !== 'widget' && part !== 'landing-page');
       
        // If we have a tenant name in URL, store it
        if (tenantNameFromUrl) {
            this.tenantName = tenantNameFromUrl;
            console.log('Stored tenant name in session:', tenantNameFromUrl);
        }
 
        if (!this.tenantName) {
            console.error('No tenant name found in URL path or session');
            this.landingPageErrorType = "Tenant name is required in URL path (e.g., /tenantname/landing-page or /tenantname/widget/landing-page)";
            this.landingPageError = true;
            return;
        }
        
        const fragmentError = new URLSearchParams(globalThis.location.hash.substring(1)).get("error");
        const queryError = this.$route.query.error;
        
        if (fragmentError || queryError) {
            const errorType = (fragmentError || queryError) as string;
            
            console.log('Error detected in URL:', errorType);
            
            // Map error codes to user-friendly messages
            if (errorType === 'credentials_not_configured') {
                this.landingPageErrorType = `Genesys credentials are not configured for tenant '${this.tenantName}'. Please contact your administrator.`;
            } else if (errorType === 'tenant_not_found') {
                this.landingPageErrorType = `Tenant '${this.tenantName}' was not found. Please check the URL and try again.`;
            } else if (errorType === 'tenant_lookup_failed') {
                this.landingPageErrorType = `Unable to verify tenant '${this.tenantName}'. Please try again or contact your administrator.`;
            } else if (errorType === 'missing_code') {
                this.landingPageErrorType = "Authorization code is missing from the callback.";
            } else {
                this.landingPageErrorType = fragmentError ? "Error getting token" : "Error getting authorisation";
            }
            
            this.landingPageError = true;
            console.log('Error state set, stopping flow');
            return; // STOP HERE - don't redirect again
        }

        const code = this.$route.query.code;
        
        if (code) {
            console.log('Authorization code found, handling callback');
            this.handleAuthCallback(code as string);
        } else {
            // Only redirect if there's NO error and NO code
            console.log('No code or error, redirecting to Genesys auth');
            this.redirectToGenesysAuth();
        }
    },
    methods: {
       async redirectToGenesysAuth() {
            try {
                const query = new StartAuthQuery(globalThis.location.href, this.tenantName);
                const backendAuthUrl = query.GetAuthUrl();
                globalThis.location.href = backendAuthUrl;
            } catch (error: any) {
                console.error("Failed to start auth:", error.message);
                this.landingPageError = true;
            }
        },

        async handleAuthCallback(code: string) {
            try {
                this.isUserAuthenticated = true;
                const query = new HandleCallbackQuery(code, this.tenantName);
                const response = await query.Execute();
                
                if (!response.ok) {
                    throw new Error("Authentication failed");
                }
                
                const authData = await response.json();
                this.authStore.tokens = {
                    accessToken: authData.tokens.accessToken,
                    refreshToken: authData.tokens.refreshToken,
                    tokenExpiry: authData.tokens.tokenExpiry
                };
                this.authStore.tokenValid = true;

                this.authStore.user = {
                    userId: authData.userModel.userId,
                    name: authData.userModel.displayName,
                    firstName: authData.userModel.firstName,
                    lastName: authData.userModel.lastName,
                    role: authData.userModel.role,
                    tenantId: authData.userModel.tenantId,
                    tenantName: this.tenantName
                };

                // Set directives
                const directives = authData.userModel.role ? [authData.userModel.role] : [];
                this.authStore.setDirectives(directives);

                // Mark as Genesys user
                this.authStore.setIsGenesysUser(true);

                // Bypass timeout for Genesys users
                this.authStore.setIsTimeOutEnabled(false);
                this.authStore.setIsLogoutEnabled(false);

                // Redirect to conversations
                setTimeout(() => this.$router.push({ name: "ViewConversations" }), 500);

            } catch (error: any) {
                console.error("Authentication callback error:", error.message);
                this.landingPageErrorType = "Failed to complete authentication";
                this.landingPageError = true;
            }
        },
    },
    data() {
        return {
            authStore: useAuthenticationStore(),
            isUserAuthenticated: false,
            landingPageError: false,
            landingPageErrorType: '',
            tenantName: '',
            authInProgress: false
        }
    }
}
</script>

<style scoped>
.landing-page {
  text-align: center;
  margin-top: 3rem;
}

.landing-page h2 {
  font-size: 2rem;
  margin-bottom: 1rem;
}

.error-message {
  text-align: center;
  color: red;
  margin-top: 1rem;
}
</style>
